import {
  UNIT_PREFIXES,
  CONTROL_TYPES,
  LOCAL_RESOURCE_MAPPED_VALUES,
  DATE_FORMAT,
  SEP_RN_DER_STATUS
} from 'utils/appConstants';
import { DER_CONTROL_TYPE_DESCRIPTION } from 'utils/appConstants';

import { Schemas } from 'utils/definitions';

import moment from 'moment';

const externallyManagedEdevs =
  window.env.REACT_APP_EXTERNALLY_MANAGED_EDEVS === 'true';

// Used for hhmmss function
const pad = num => {
  return ('0' + num).slice(-2);
};
// Convert unix time to hh:mm:ss for duration outputs
export const hhmmss = secs => {
  var minutes = Math.floor(secs / 60);
  secs = secs % 60;
  var hours = Math.floor(minutes / 60);
  minutes = minutes % 60;
  return `${pad(hours)}:${pad(minutes)}:${pad(secs)}`;
};

export const getEndDateTime = (
  startDateTime,
  durationHours,
  durationMinutes,
  durationSeconds
) => {
  const hours = parseInt(durationHours) ? Math.round(durationHours) : 0;
  const minutes = parseInt(durationMinutes) ? Math.round(durationMinutes) : 0;
  const seconds = parseInt(durationSeconds) ? Math.round(durationSeconds) : 0;
  const secondsTime = hours * 3600 + minutes * 60 + seconds;
  const start = moment(startDateTime, 'LLLL').unix();
  const endUnix = start + secondsTime;
  const endString = moment.unix(endUnix).toDate();
  return endString;
};

export const getStartDateTime = (
  startDateTime,
  durationHours,
  durationMinutes,
  durationSeconds
) => {
  const hours = parseInt(durationHours) ? Math.round(durationHours) : 0;
  const minutes = parseInt(durationMinutes) ? Math.round(durationMinutes) : 0;
  const seconds = parseInt(durationSeconds) ? Math.round(durationSeconds) : 0;
  const secondsTime = hours * 3600 + minutes * 60 + seconds;
  const start = moment(startDateTime, 'LLLL').unix();
  const endUnix = start + secondsTime;
  const endString = moment.unix(endUnix).toDate();
  return endString;
};

/**
 * Takes a number and converts it into a formatted time.
 * Example: 12300 hundredths of seconds becomes 2 mins 3 secs
 * @param {number} hundredthsOfSeconds - hundredths of a second
 * @param {boolean} allowDecimals - if decimals should be included in the format. If not,
 * the number will be rounded if there is a decimal.
 * @param {boolean} roundDownToMinutes - if we want to only show minutes - no seconds.
 */
export const durationFormatter = (
  hundredthsOfSeconds,
  allowDecimals,
  roundDownToMinutes = false
) => {
  let seconds = hundredthsOfSeconds / 100;
  let decimal = '';
  if (allowDecimals) {
    decimal = parseFloat((seconds - Math.floor(seconds)).toFixed(2));
  } else {
    seconds = Math.round(seconds);
  }
  const duration = moment.duration(seconds * 1000);
  const convertedDurations = {
    years: { value: duration.years(), unit: 'yr' },
    months: { value: duration.months(), unit: 'mon' },
    days: { value: duration.days(), unit: 'day' },
    hours: { value: duration.hours(), unit: 'hr' },
    minutes: { value: duration.minutes(), unit: 'min' },
    seconds: {
      value: allowDecimals
        ? `${parseInt(duration.seconds()) + decimal}`
        : duration.seconds(),
      unit: 'sec'
    }
  };
  const orderedTimeGroups = ['years', 'months', 'days', 'hours', 'minutes'];
  if (!roundDownToMinutes) {
    orderedTimeGroups.push('seconds');
  }
  let formattedTime = '';
  orderedTimeGroups.forEach(timeGrouping => {
    const timeGroupingValue = convertedDurations[timeGrouping].value;
    const timeGroupingUnit = convertedDurations[timeGrouping].unit;
    if (timeGroupingValue) {
      formattedTime += `${timeGroupingValue} ${timeGroupingUnit}`;
      if (timeGroupingValue > 1) {
        formattedTime += 's';
      }
      formattedTime += ' ';
    }
  });
  if (formattedTime.trim() === '') {
    return '0 secs';
  }
  return formattedTime;
};

/**
 * A number gets returned as an object with more friendly values.
 * This function provides data for formatting, but does not
 * format the number itself in the return.
 *
 * All numbers are reduced to a whole number with two decimal places
 * if applicable, otherwise just the whole number.
 *
 * This function can be used to reduce a number to a number with an SI Unit
 * (and are provided with both an abbreviation and the full name),
 * or to gather which power of 10 grouping it falls in (1<=x<1000).
 *
 * An example: The number 1234.56 will return with
 * {prefix: 'k', name: 'kilo', multiplier: 3, reducedNumber: 1.23 }
 *
 * With this, you can format data how you want. Example:
 * (`${format[reducedNumber]}${format[prefix]}`)
 * @param {number} number
 * @return returnObj - The data used to format any numbers.
 * @return returnObj.prefix The abbreviation of the SI Unit
 * @return returnObj.name The full name of the SI Unit
 * @return returnObj.multiplier The power of 10 required to have the number fit in (1<=x<1000)
 * @return returnObj.reducedNumber The number reduced by the multiplier/power of ten.
 */
export const formatNumberWithPrefixAndMultiplier = number => {
  if (typeof number !== 'number') {
    throw new Error('Not a proper numerical input');
  }
  let prefix = '';
  let name = '';
  let assignedPrefix = '';
  let absoluteNumber = Math.abs(number);
  let reducedNumber = number;
  let multiplier = 0;

  if (absoluteNumber >= 1000000000) {
    multiplier = 9;
    assignedPrefix = 'GIGA';
  } else if (absoluteNumber >= 1000000) {
    multiplier = 6;
    assignedPrefix = 'MEGA';
  } else if (absoluteNumber >= 1000) {
    multiplier = 3;
    assignedPrefix = 'KILO';
  } else if (absoluteNumber === 0 || absoluteNumber >= 0.01) {
    multiplier = 0;
    assignedPrefix = '';
  } else if (absoluteNumber >= 0.001) {
    multiplier = -3;
    assignedPrefix = 'MILLI';
  } else if (absoluteNumber >= 0.000001) {
    multiplier = -6;
    assignedPrefix = 'MICRO';
  } else {
    multiplier = -9;
    assignedPrefix = 'NANO';
  }
  if (assignedPrefix) {
    prefix = UNIT_PREFIXES[assignedPrefix].prefix;
    name = UNIT_PREFIXES[assignedPrefix].name;
  }
  reducedNumber = number / Math.pow(10, multiplier);
  reducedNumber = Number.isInteger(reducedNumber)
    ? reducedNumber
    : parseFloat(reducedNumber.toFixed(2));
  return { prefix, name, reducedNumber, multiplier };
};

/**
 * Takes all the controlTypes of a control and puts them into a controlTypes object.
 * @param {object} control - the control or default control we are wanting to format.
 */
export const formatControlTypes = control => {
  if (!control) {
    return {};
  }
  const filteredControl = {
    controlTypes: {}
  };
  for (let controlProperty in control) {
    if (CONTROL_TYPES[controlProperty]) {
      filteredControl.controlTypes[controlProperty] = control[controlProperty];
    } else {
      filteredControl[controlProperty] = control[controlProperty];
    }
  }
  return filteredControl;
};

/**
 * Takes an object and returns a filtered version with only properties
 * that are listed in the allowedProperties array.
 * @param {object} node - the indeNode we are filtering properties for.
 * @param {Array} allowedProperties - a list of the names of properties in the node object we will allow.
 * @param {boolean} filterNullsAndUndefined - if true, filter out any properties with a value of
 * null or undefined.
 * @return {object} - return a filtered object.
 */
export const filterForAllowedProperties = (
  node,
  allowedProperties,
  filterNullsAndUndefined
) => {
  const filteredNode = {};
  for (let property in node) {
    if (allowedProperties.includes(property)) {
      if (
        !filterNullsAndUndefined ||
        (filterNullsAndUndefined &&
          node[property] !== null &&
          node[property] !== undefined)
      ) {
        filteredNode[property] = node[property];
      }
    }
  }
  return filteredNode;
};

/**
 * Converts seconds into hours/minutes/seconds
 * @param {integer} timeInSeconds
 * @return {object} hours, minutes, seconds
 */
export const convertTohhmmss = timeInSeconds => {
  let hours = Math.floor(timeInSeconds / 60 / 60);
  let minutes = Math.floor(timeInSeconds / 60) - hours * 60;
  let seconds = Math.floor(timeInSeconds % 60);
  return { hours, minutes, seconds };
};

/**
 *
 * @param {object} time - object with hours, minutes, seconds in integers
 * @return {integer} seconds - time in seconds
 */
export const convertToSeconds = ({ hours = 0, minutes = 0, seconds = 0 }) => {
  if (
    Number.isInteger(hours) ||
    Number.isInteger(minutes) ||
    Number.isInteger(seconds)
  ) {
    hours = hours || 0;
    minutes = minutes || 0;
    seconds = seconds || 0;
  }
  return hours * 60 * 60 + minutes * 60 + seconds;
};

/**
 * @function: derInfoDataConverter
 * @description Convert general device information for dderc, dera, derg, or dercap
 * @param {Object} deviceInfo either dderc, dera, derg, or dercap object
 * @param {object} deviceCategory sep-common / standard variable naming for dderc, dera, derg, or dercap
 * @returns {Object} {description, value} data object to be used with react table
 */
export const derInfoDataConverter = (deviceInfo, deviceCategory) => {
  const rtnData = [];

  if (deviceInfo) {
    for (const deviceProperty of Object.keys(deviceInfo)) {
      if (
        Object.prototype.hasOwnProperty.call(
          Schemas[deviceCategory],
          deviceProperty
        ) === true &&
        Object.prototype.hasOwnProperty.call(
          Schemas[deviceCategory][deviceProperty],
          'toDisplayString'
        ) === true
      ) {
        const displayString = Schemas[deviceCategory][
          deviceProperty
        ].toDisplayString(
          deviceInfo[deviceProperty],
          Schemas[deviceCategory][deviceProperty].powerOfTen,
          Schemas[deviceCategory][deviceProperty].displayUnits
        );

        rtnData.push({
          deviceProperty,
          value: displayString
        });
      }
    }
  }

  return rtnData;
};

/**
 * @description Convert DER Status device information
 * @param {Object} statusInfo ders object
 * @returns {Object} {description, value} data object to be used with react table
 */
export const derStatusDataConverter = statusInfo => {
  const rtnData = [];

  // this ignore list are values that we don't want
  // in the table.
  if (statusInfo !== null && statusInfo !== undefined) {
    for (const deviceProperty of Object.keys(statusInfo)) {
      const derStatusValue = statusInfo[deviceProperty].value;
      const derStatusDateTime = statusInfo[deviceProperty].dateTime;
      if (
        Object.prototype.hasOwnProperty.call(
          Schemas[SEP_RN_DER_STATUS],
          deviceProperty
        ) === true &&
        Object.prototype.hasOwnProperty.call(
          Schemas[SEP_RN_DER_STATUS][deviceProperty],
          'toDisplayString'
        ) === true
      ) {
        const displayString = Schemas[SEP_RN_DER_STATUS][
          deviceProperty
        ].toDisplayString(
          derStatusValue,
          Schemas[SEP_RN_DER_STATUS][deviceProperty].powerOfTen,
          Schemas[SEP_RN_DER_STATUS][deviceProperty].displayUnits
        );

        rtnData.push({
          deviceProperty,
          time: moment.unix(derStatusDateTime).format(DATE_FORMAT),
          value: displayString
        });
      }
    }
  }
  return rtnData;
};

/**
 * Using Math.pow() with a negative exponent power can create extremely long decimals,
 * which JavaScript displays as either scientific notation (e.g. 10e^-8),
 * or with extra digits as a converted floating point decimal
 * (e.g. .012 being converted to .012000000000998).
 * The goal is to display an accurate representation of the user's input number
 * that is stored in the database as a `num` and `multiplier`.
 * @param {number} num - the number to be multiplied
 * @param {number} multiplier - the power of ten to multiply with
 * @return {number} A display number without the floating point issue
 */
export const formatCurveDataValues = (num, multiplier) => {
  if (num === 0) {
    return 0;
  }
  if (multiplier >= 0) {
    return (num * Math.pow(10, multiplier)).toFixed(2);
  }
  const powed = num * Math.pow(10, multiplier);
  let numSI = 10;
  let displayDecimals = 0;
  for (; num % numSI === 0; numSI *= 10, displayDecimals++) {}
  let myFixed = Math.abs(multiplier) - displayDecimals;
  if (myFixed <= 2) {
    myFixed = 2;
  }
  return powed.toFixed(myFixed);
};

/**
 * Displays floating point numbers with significant digits up to the
 * length of the absolute value of the multiplier
 * @param {number} number - number to be turned into floating point
 * @param {number} multiplier - multiplier, expected to be 0 or negative.
 */
export const displayFloatingPoint = (number, multiplier = 0) => {
  const parsedAsFloatNumber = parseFloat(number);
  let value = parsedAsFloatNumber * Math.pow(10, multiplier);

  //Check if there is a decimal.
  if (multiplier < 1) {
    value = value.toFixed(Math.abs(multiplier));
  }
  return value;
};

/**
 * Takes a resource (e.g. a curve), and checks its local value (e.g. localCurve) to
 * determine whether it is externally managed or not
 * @param {integer} value - The local/remote integer value
 * @param {boolean} boolean - If the return needs to be boolean, this should be set to true
 * @returns {string || boolean} - Returns either 'True' or 'False' dependent on if the resource is
 * externally managed.
 */
export const isExternallyManaged = (value, returnBoolean) => {
  if (!isNaN(value) && !externallyManagedEdevs) {
    throw new Error(
      'Should not check externally managed if feature flag is false'
    );
  }
  if (value === LOCAL_RESOURCE_MAPPED_VALUES.LOCAL) {
    return returnBoolean ? false : 'False';
  }
  return returnBoolean ? true : 'True';
};

/**
 * @description Decodes the bitmap from any bit array list
 * returns an array of values if that bit is on
 *
 * @param bitmap {string} a hexidecimal bitmap.
 * @param bitArrayList {string[] | array} a hexidecimal bitmap.
 * @return {string[] | array} an array of control modes if supported. if none supported, empty array.
 */
const listFromBitmap = (bitmap, bitArrayList) => {
  const controlTypes = [];
  // parse hex to int
  const parsedBitmap = parseInt(bitmap, 16);
  // i is index, bm is bitmask, if the index is less than the number of array list, loop, increase index
  // and shift bitmask
  for (let i = 0, bm = 1; i < bitArrayList.length; i++, bm = bm << 1) {
    // if bit is set, push the control types info to array
    if ((parsedBitmap & bm) !== 0) {
      controlTypes.push(bitArrayList[i]);
    }
  }

  return controlTypes;
};

/**
 * @overview function that handles decoding derControlTypeDescription bitmap
 */
/**
 * @description Decodes the bitmap from any DERControlTypeDescriptions column
 * returns an array with control descriptions if that bit is on
 *
 * @param derControlBitmap {string} a hexidecimal bitmap that defines control modes supported by DER.
 * @return {string[] | array} an array of control mode descriptions if supported. if none supported, empty array.
 *
 */
export const controlTypeDescriptionsFromBitmap = derControlBitmap => {
  return listFromBitmap(derControlBitmap, DER_CONTROL_TYPE_DESCRIPTION);
};
/**
 * @function: calculateValueAndPowerOfTen
 * @description calculates the product of a number and a power-of-ten
 * @param {Object} value the displacement-multiplier value
 * @param {integer} powerOfTen a powerOfTen, used for significant figures for some values
 * @returns {integer} the product of the number and 10^powerOfTen
 */
export function calculateValueAndPowerOfTen(value, powerOfTen) {
  return value * Math.pow(10, powerOfTen);
}

/**
 * @function: formatDerInfoSeconds
 * @description Converts a seconds field to a duration formatted string for display in a panel
 * @param {Object} value the displacement-multiplier value
 * @param {integer} powerOfTen a powerOfTen, used for significant figures for some values
 * @param {String} displayUnits a unit string to append at the end of the display string
 * @returns {String} a string for display in a panel
 */
// display units are irrelevant, but including them to show the pattern
export function formatDerInfoSeconds(value, powerOfTen, displayUnits) {
  let secondsSigFigs = value;
  if (powerOfTen !== undefined) {
    secondsSigFigs = calculateValueAndPowerOfTen(value, powerOfTen);
  }

  return durationFormatter(parseFloat(secondsSigFigs));
}

/**
 * @function: displayFormatNumberWithPrefixAndMultiplier
 * @description Converts a number field to a string for display in a panel
 * @param {Object} value the displacement-multiplier value
 * @param {integer} powerOfTen a powerOfTen, used for significant figures for some values
 * @param {String} displayUnits a unit string to append at the end of the display string
 * @returns {String} a string for display in a panel
 */
export function displayFormattedNumberWithPrefixAndMultiplier(
  value,
  powerOfTen,
  displayUnits
) {
  const valueAndMult = calculateValueAndPowerOfTen(value, powerOfTen);

  const formattedValueObj = formatNumberWithPrefixAndMultiplier(valueAndMult);
  return `${formattedValueObj.reducedNumber}${formattedValueObj.prefix}${displayUnits}`;
}

/**
 * @function: displayValueWithMultiplier
 * @description Converts a field that has value and multiplier to a string for display in a panel.
 *              Note: multiplier is a power-of-ten multiplier
 * @param {Object} value the displacement-multiplier value
 * @param {integer} powerOfTen a powerOfTen, used for significant figures for some values
 * @param {String} displayUnits a unit string to append at the end of the display string
 * @returns {String} a string for display in a panel
 */
export function displayValueWithMultiplier(value, powerOfTen, displayUnits) {
  if (
    Object.prototype.hasOwnProperty.call(value, 'value') === false ||
    Object.prototype.hasOwnProperty.call(value, 'multiplier') === false
  ) {
    throw new Error(
      'This function may only be called on values containing value and multiplier'
    );
  }
  return displayFormattedNumberWithPrefixAndMultiplier(
    value.value,
    value.multiplier,
    displayUnits
  );
}

/**
 * @function: displayDisplacementWithMultiplier
 * @description Converts a field that has displacement and multiplier to a string for display in a panel
 *              Note: multiplier is a power-of-ten multiplier
 * @param {Object} value the displacement-multiplier value
 * @param {integer} powerOfTen a powerOfTen, used for significant figures for some values
 * @param {String} displayUnits a unit string to append at the end of the display string
 * @returns {String} a string for display in a panel
 */
export function displayDisplacementWithMultiplier(
  value,
  powerOfTen,
  displayUnits
) {
  if (
    Object.prototype.hasOwnProperty.call(value, 'displacement') === false ||
    Object.prototype.hasOwnProperty.call(value, 'multiplier') === false
  ) {
    throw new Error(
      'This function may only be called on values containing displacement and multiplier'
    );
  }
  return displayFormattedNumberWithPrefixAndMultiplier(
    value.displacement,
    value.multiplier,
    displayUnits
  );
}

/**
 * @function: displayGradW
 * @description Converts a setGradW or setSoftGradW for display in a panel
 * @param {Object} value the GradW value
 * @param {integer} powerOfTen a powerOfTen, used for significant figures for some values
 * @param {String} displayUnits a unit string to append at the end of the display string
 * @returns {String} a string for display in a panel
 */
export function displayGradW(value, powerOfTen, displayUnits) {
  if (parseInt(value) === 0) {
    return 'No Limit';
  }
  return displayFormattedNumberWithPrefixAndMultiplier(
    value,
    powerOfTen,
    displayUnits
  );
}

/**
 * @function: displayDateTime
 * @description Converts a date-time value to a string for display in a panel
 * @param {Object} value the date-time value
 * @param {integer} powerOfTen a powerOfTen, used for significant figures for some values
 * @param {String} displayUnits a unit string to append at the end of the display string
 * @returns {String} a string for display in a panel
 */
export function displayDateTime(value, powerOfTen, displayUnits) {
  return moment.unix(value).format(DATE_FORMAT);
}

/**
 * @function: displayString
 * @description Converts a value to a string for display in a panel
 * @param {Object} value the value
 * @returns {String} a string for display in a panel
 */
export function displayString(value) {
  if (value !== null && value !== undefined) {
    return value.toString();
  } else {
    return '';
  }
}
